import { ParticleEffect } from './ParticleEffect';
import type { Particle, IntensityColorScheme } from '../types';

/**
 * Summer heat/dust effect
 */
export class SummerEffect extends ParticleEffect {
    protected getDefaultVariant(): string {
        return 'sunDust';
    }

    protected getDefaultColorScheme(): IntensityColorScheme {
        return {
            minimal: '#fffacd',  // Lemon chiffon
            subtle: '#ffff88',   // Light yellow
            moderate: '#ffd700', // Gold
            bold: '#ffa500',     // Orange
        };
    }

    init(width: number, height: number): void {
        const count = Math.floor(this.getParticleCount() * 0.5); // Less particles for summer
        this.particles = [];

        for (let i = 0; i < count; i++) {
            this.particles.push(this.createParticle(width, height));
        }
    }

    private createParticle(width: number, height: number): Particle {
        const isFirefly = this.variant === 'fireflies';
        const sizeMultiplier = this.getSizeMultiplier();
        const speedMultiplier = this.getSpeedMultiplier();
        const useIntensityColor = this.customization.intensityColors || this.customization.customColor;

        const defaultColor = isFirefly ? '#ffff88' : '#ffffffaa';
        const particleColor = useIntensityColor ? this.getIntensityColor() : defaultColor;

        const particle: Particle = {
            x: Math.random() * width,
            y: Math.random() * height,
            vx: (Math.random() - 0.5) * 0.3 * speedMultiplier,
            vy: (isFirefly ? (Math.random() - 0.5) * 0.5 : -(0.1 + Math.random() * 0.3)) * speedMultiplier, // Float up
            size: (isFirefly ? 2 + Math.random() * 2 : 1 + Math.random() * 2) * sizeMultiplier,
            opacity: isFirefly ? 0.3 + Math.random() * 0.4 : 0.1 + Math.random() * 0.2,
            rotation: 0,
            rotationSpeed: 0,
            color: particleColor,
            type: this.variant,
        };

        // Try to add custom image if available
        if (this.hasCustomImages()) {
            particle.image = this.getRandomImage();
        }

        return particle;
    }

    update(_deltaTime: number, _width: number, height: number): void {
        // Ensure particles have images if they loaded after particle creation
        this.ensureParticleImages();

        for (let i = 0; i < this.particles.length; i++) {
            const p = this.particles[i];
            const time = Date.now() / 1000;

            // Gentle floating motion
            p.x += p.vx + Math.sin(time + i) * 0.1;
            p.y += p.vy;

            // Firefly flickering effect
            if (p.type === 'fireflies') {
                p.opacity = 0.3 + Math.sin(time * 3 + i) * 0.3;
            }

            // Reset if off screen
            if (p.y < -10) {
                p.y = height + 10;
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const p of this.particles) {
            // Use custom image if available
            if (p.image && p.image.complete && p.image.naturalHeight > 0) {
                this.renderParticle(ctx, p);
            } else {
                // Default rendering
                ctx.save();
                ctx.globalAlpha = p.opacity;

                if (p.type === 'fireflies') {
                    // Add glow effect
                    const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
                    gradient.addColorStop(0, p.color!);
                    gradient.addColorStop(1, 'transparent');
                    ctx.fillStyle = gradient;
                } else {
                    ctx.fillStyle = p.color!;
                }

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();

                ctx.restore();
            }
        }
    }
}
